import config from '../config/environment';
import { handleServiceError } from '../utils/errorHandler';
import supabase from '../config/supabase';
import type { OrchestratorRow } from '../types/orchestrator';
import type { PaginatedResponse, PaginationInfo } from '../types/student';
import { mapProjectIdentity } from '../utils/project';
import { readIsRecentlyReactivated } from '../utils/studentReactivation';
import {
  readIsPregnant,
  readIsRedFlag,
  readIsSensitive,
  readPregnantUntil,
  readSensitiveReason,
} from '../utils/studentSensitive';

export type OrchestratorPaginatedResponse = PaginatedResponse<OrchestratorRow>;

class OrchestratorServiceClass {
  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: {
      searchName?: string;
      filterPaymentStatus?: string[];
      filterPaymentMethod?: string[];
      filterWorkoutStatus?: string[];
      filterFupStatus?: string[];
      filterFupDate?: string;
      filterCoachIds?: string[];
      filterGateway?: string[];
      filterStudentTenure?: string;
      classifications?: string[];
      customDateStart?: string;
      customDateEnd?: string;
      dueDateStart?: string;
      dueDateEnd?: string;
      lastWorkoutDateStart?: string;
      lastWorkoutDateEnd?: string;
      projectCodes?: string[];
      sortOrder?: string;
    } = {},
  ): Promise<OrchestratorPaginatedResponse> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const url = new URL(`${config.supabaseFunctionsUrl}/list-paginated-orchestrator`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));
    if (filters.searchName) url.searchParams.set('searchName', filters.searchName);
    if (filters.filterPaymentStatus?.length) url.searchParams.set('filterPaymentStatus', filters.filterPaymentStatus.join(','));
    if (filters.filterPaymentMethod?.length) url.searchParams.set('filterPaymentMethod', filters.filterPaymentMethod.join(','));
    if (filters.filterWorkoutStatus?.length) url.searchParams.set('filterWorkoutStatus', filters.filterWorkoutStatus.join(','));
    if (filters.filterFupStatus?.length) url.searchParams.set('filterFupStatus', filters.filterFupStatus.join(','));
    if (filters.filterFupDate) url.searchParams.set('filterFupDate', filters.filterFupDate);
    if (filters.filterCoachIds?.length) url.searchParams.set('filterCoachIds', filters.filterCoachIds.join(','));
    if (filters.filterGateway?.length) url.searchParams.set('filterGateway', filters.filterGateway.join(','));
    if (filters.classifications?.length) {
      url.searchParams.set('classifications', filters.classifications.join(','));
    } else if (filters.filterStudentTenure && filters.filterStudentTenure !== 'all') {
      url.searchParams.set('filterStudentTenure', filters.filterStudentTenure);
    }
    if (filters.customDateStart) url.searchParams.set('customDateStart', filters.customDateStart);
    if (filters.customDateEnd) url.searchParams.set('customDateEnd', filters.customDateEnd);
    if (filters.dueDateStart) url.searchParams.set('dueDateStart', filters.dueDateStart);
    if (filters.dueDateEnd) url.searchParams.set('dueDateEnd', filters.dueDateEnd);
    if (filters.lastWorkoutDateStart) url.searchParams.set('lastWorkoutDateStart', filters.lastWorkoutDateStart);
    if (filters.lastWorkoutDateEnd) url.searchParams.set('lastWorkoutDateEnd', filters.lastWorkoutDateEnd);
    if (filters.projectCodes?.length) url.searchParams.set('projectCodes', filters.projectCodes.join(','));
    if (filters.sortOrder) url.searchParams.set('sortOrder', filters.sortOrder);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(new Error(err?.error || 'Erro ao listar orquestrador'), 'listar orquestrador');
    }

    const body = await response.json() as OrchestratorPaginatedResponse & {
      pagination?: Partial<PaginationInfo>;
    };

    const pagination = body.pagination ?? {};
    const resolvedPageSize = Number(pagination.pageSize ?? pageSize);
    const resolvedTotalRecords = Number(pagination.totalRecords ?? 0);
    const resolvedTotalPages = Number.isFinite(Number(pagination.totalPages))
      ? Number(pagination.totalPages)
      : (resolvedTotalRecords === 0 ? 0 : Math.ceil(resolvedTotalRecords / resolvedPageSize));

    return {
      data: (Array.isArray(body.data) ? body.data : []).map((row) => ({
        ...row,
        project: mapProjectIdentity(row.project) ?? mapProjectIdentity(row),
        is_reactivated: readIsRecentlyReactivated(row),
        is_sensitive: readIsSensitive(row),
        sensitive_reason: readSensitiveReason(row),
        is_pregnant: readIsPregnant(row),
        pregnant_until: readPregnantUntil(row),
        is_red_flag: readIsRedFlag(row),
      })),
      pagination: {
        page: Number(pagination.page ?? page),
        pageSize: resolvedPageSize,
        totalRecords: Number.isFinite(resolvedTotalRecords) ? resolvedTotalRecords : 0,
        totalPages: resolvedTotalPages,
        hasNextPage: Boolean(pagination.hasNextPage ?? (resolvedTotalPages > 0 && Number(pagination.page ?? page) < resolvedTotalPages)),
        hasPreviousPage: Boolean(pagination.hasPreviousPage ?? Number(pagination.page ?? page) > 1),
      },
    };
  }
}

export const OrchestratorService = new OrchestratorServiceClass();
